import { useState, useCallback, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { PackCard, PackId, PackSummary, Rarity, Variant } from "../types";
import { CharacterCard } from "./CharacterCard";

interface PackOpeningProps {
  cards: PackCard[];
  pack?: PackSummary | null;
  onDone: () => void;
  onOpenAnother: () => void;
  canAfford: boolean;
  loading: boolean;
  openAnotherLabel?: string;
}

type Phase = "entering" | "landed" | "unwrapping" | "revealing" | "done";

const RARITY_MESSAGES: Record<Rarity, string[]> = {
  common: ["Common pull", "Nothing special", "Expected"],
  uncommon: ["Not bad!", "Uncommon find", "Decent pull"],
  rare: ["Nice pull!", "Rare find!", "Let's go!"],
  epic: ["EPIC PULL!", "Incredible!", "So lucky!"],
  legendary: ["LEGENDARY!!!", "UNBELIEVABLE!", "JACKPOT!"],
};

const RARITY_COLOR: Record<Rarity, string> = {
  common: "text-zinc-400",
  uncommon: "text-emerald-400",
  rare: "text-blue-400",
  epic: "text-purple-400",
  legendary: "text-yellow-400",
};

const DOT_COLOR: Record<Rarity, string> = {
  common: "bg-zinc-400",
  uncommon: "bg-emerald-400",
  rare: "bg-blue-400",
  epic: "bg-purple-400",
  legendary: "bg-yellow-400",
};

const DOT_GLOW: Record<Rarity, string> = {
  common: "shadow-zinc-400/30",
  uncommon: "shadow-emerald-400/40",
  rare: "shadow-blue-400/40",
  epic: "shadow-purple-400/50",
  legendary: "shadow-yellow-400/50",
};

interface PackTheme {
  wrapperGradientClass: string;
  crosshairClass: string;
  sealClass: string;
  boxGradientClass: string;
  boxBorderClass: string;
  titleClass: string;
  subtitleClass: string;
  dividerClass: string;
  cardsLabelClass: string;
  promptClass: string;
  backdropClass: string;
  auraClass: string;
  auraRingClass: string;
  tagline: string;
  sparkles: boolean;
}

const PACK_THEMES: Record<PackId, PackTheme> = {
  standard: {
    wrapperGradientClass: "from-amber-700/40 via-amber-600/20 to-amber-800/40",
    crosshairClass: "bg-amber-400/30",
    sealClass: "border-amber-400/50 text-amber-400/70",
    boxGradientClass: "from-zinc-700 via-zinc-800 to-zinc-900",
    boxBorderClass: "border-zinc-600",
    titleClass: "text-zinc-300",
    subtitleClass: "text-zinc-500",
    dividerClass: "bg-zinc-600",
    cardsLabelClass: "text-zinc-500",
    promptClass: "text-muted-foreground",
    backdropClass: "pack-scene-backdrop--standard",
    auraClass: "pack-aura--standard",
    auraRingClass: "pack-aura-ring--standard",
    tagline: "Balanced odds",
    sparkles: false,
  },
  boosted: {
    wrapperGradientClass: "from-cyan-600/45 via-blue-500/25 to-indigo-700/40",
    crosshairClass: "bg-cyan-300/45",
    sealClass: "border-cyan-200/60 text-cyan-100/80",
    boxGradientClass: "from-sky-700 via-blue-800 to-indigo-950",
    boxBorderClass: "border-blue-300/60",
    titleClass: "text-sky-100",
    subtitleClass: "text-cyan-200/75",
    dividerClass: "bg-cyan-200/50",
    cardsLabelClass: "text-cyan-100/75",
    promptClass: "text-cyan-100/80",
    backdropClass: "pack-scene-backdrop--boosted",
    auraClass: "pack-aura--boosted",
    auraRingClass: "pack-aura-ring--boosted",
    tagline: "Rare+ odds boosted",
    sparkles: true,
  },
  elite: {
    wrapperGradientClass: "from-amber-500/45 via-yellow-400/25 to-orange-600/45",
    crosshairClass: "bg-yellow-200/55",
    sealClass: "border-yellow-200/70 text-yellow-100/90",
    boxGradientClass: "from-amber-900 via-yellow-900/80 to-zinc-950",
    boxBorderClass: "border-yellow-300/65",
    titleClass: "text-yellow-100",
    subtitleClass: "text-amber-200/80",
    dividerClass: "bg-yellow-200/60",
    cardsLabelClass: "text-yellow-100/85",
    promptClass: "text-yellow-100/90",
    backdropClass: "pack-scene-backdrop--elite",
    auraClass: "pack-aura--elite",
    auraRingClass: "pack-aura-ring--elite",
    tagline: "",
    sparkles: true,
  },
  mythic: {
    wrapperGradientClass: "from-rose-700/50 via-orange-500/25 to-yellow-500/30",
    crosshairClass: "bg-rose-200/55",
    sealClass: "border-rose-200/70 text-rose-100/90",
    boxGradientClass: "from-rose-950 via-orange-950/90 to-zinc-950",
    boxBorderClass: "border-rose-300/65",
    titleClass: "text-rose-100",
    subtitleClass: "text-orange-200/80",
    dividerClass: "bg-rose-200/60",
    cardsLabelClass: "text-orange-100/85",
    promptClass: "text-orange-100/90",
    backdropClass: "pack-scene-backdrop--mythic",
    auraClass: "pack-aura--mythic",
    auraRingClass: "pack-aura-ring--mythic",
    tagline: "Epic + Legendary only",
    sparkles: true,
  },
};

function getRevealMessage(rarity: Rarity) {
  const messages = RARITY_MESSAGES[rarity];
  return messages[Math.floor(Math.random() * messages.length)] ?? messages[0] ?? "Nice pull!";
}

function getGlowStyle(rarity: Rarity, variant: Variant): React.CSSProperties {
  const multipliers: Record<Variant, number> = {
    normal: 1,
    shiny: 1.2,
    rainbow: 1.4,
    glitch: 1.3,
  };
  const m = multipliers[variant];

  switch (rarity) {
    case "common":
      return {
        boxShadow: `0 0 ${10 * m}px rgba(161, 161, 170, ${0.15 * m})`,
      };
    case "uncommon":
      return {
        boxShadow: `0 0 ${20 * m}px rgba(52, 211, 153, ${0.35 * m})`,
      };
    case "rare":
      return {
        boxShadow: `0 0 ${25 * m}px rgba(96, 165, 250, ${0.45 * m}), 0 0 ${50 * m}px rgba(96, 165, 250, ${0.2 * m})`,
      };
    case "epic":
      return {
        boxShadow: `0 0 ${35 * m}px rgba(168, 85, 247, ${0.55 * m}), 0 0 ${60 * m}px rgba(168, 85, 247, ${0.3 * m}), 0 0 ${80 * m}px rgba(168, 85, 247, ${0.15 * m}), inset 0 0 ${20 * m}px rgba(168, 85, 247, ${0.1 * m})`,
      };
    case "legendary": {
      const base = `0 0 ${50 * m}px rgba(250, 204, 21, ${0.7 * m}), 0 0 ${80 * m}px rgba(250, 204, 21, ${0.4 * m}), 0 0 ${120 * m}px rgba(250, 204, 21, ${0.2 * m}), 0 0 ${160 * m}px rgba(250, 204, 21, ${0.1 * m})`;
      if (variant === "rainbow") {
        return {
          boxShadow: `${base}, 0 0 ${40 * m}px rgba(255, 100, 100, 0.2), 0 0 ${40 * m}px rgba(100, 255, 100, 0.2), 0 0 ${40 * m}px rgba(100, 100, 255, 0.2)`,
        };
      }
      return { boxShadow: base };
    }
    default:
      return {};
  }
}

function getGlowPulseClass(rarity: Rarity): string {
  if (rarity === "legendary") return "glow-pulse-fast";
  if (rarity === "epic") return "glow-pulse";
  return "";
}

function getStatusCount(card: Pick<PackCard, "isDuplicate" | "isUpgrade">): number {
  return Number(card.isDuplicate) + Number(card.isUpgrade);
}

function getStatusPaddingClass(card: Pick<PackCard, "isDuplicate" | "isUpgrade">): string {
  const count = getStatusCount(card);
  if (count >= 2) return "pb-14";
  if (count === 1) return "pb-9";
  return "";
}

function getStatusOffsetClass(card: PackCard | undefined): string {
  if (!card) return "";
  const count = getStatusCount(card);
  if (count >= 2) return "mb-14";
  if (count === 1) return "mb-10";
  return "";
}

function CardStatusBadges({ card }: { card: PackCard }) {
  if (!card.isDuplicate && !card.isUpgrade) return null;

  return (
    <div className="absolute left-1/2 -translate-x-1/2 bottom-0 z-30 pointer-events-none flex flex-col items-center gap-1">
      {card.isDuplicate && (
        <div className="bg-yellow-500/95 text-black font-bold text-xs px-3 py-1.5 rounded-full shadow-lg whitespace-nowrap">
          DUPE +{card.coinsGained.toLocaleString()}
        </div>
      )}
      {card.isUpgrade && (
        <div className="bg-cyan-400/95 text-black font-bold text-xs px-3 py-1.5 rounded-full shadow-lg animate-pulse whitespace-nowrap">
          UPGRADE!
        </div>
      )}
    </div>
  );
}

export function PackOpening({
  cards,
  pack,
  onDone,
  onOpenAnother,
  canAfford,
  loading,
  openAnotherLabel,
}: PackOpeningProps) {
  const [phase, setPhase] = useState<Phase>("entering");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [revealedCards, setRevealedCards] = useState<Set<number>>(new Set());
  const [dismissedCards, setDismissedCards] = useState<Set<number>>(new Set());
  const [isAnimating, setIsAnimating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageKey, setMessageKey] = useState(0);
  const [lastRarity, setLastRarity] = useState<Rarity | null>(null);
  const [screenFlash, setScreenFlash] = useState<Rarity | null>(null);
  const timeoutRefs = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  const scheduleTimeout = useCallback((fn: () => void, delayMs: number) => {
    const timeoutId = setTimeout(() => {
      timeoutRefs.current = timeoutRefs.current.filter((id) => id !== timeoutId);
      fn();
    }, delayMs);
    timeoutRefs.current.push(timeoutId);
    return timeoutId;
  }, []);

  const clearScheduledTimeouts = useCallback(() => {
    for (const timeoutId of timeoutRefs.current) {
      clearTimeout(timeoutId);
    }
    timeoutRefs.current = [];
  }, []);

  useEffect(() => {
    return () => {
      clearScheduledTimeouts();
    };
  }, [clearScheduledTimeouts]);

  // Reset the full reveal flow whenever a new pack payload arrives.
  useEffect(() => {
    clearScheduledTimeouts();
    setPhase("entering");
    setCurrentIndex(0);
    setRevealedCards(new Set());
    setDismissedCards(new Set());
    setIsAnimating(false);
    setMessage(null);
    setMessageKey(0);
    setLastRarity(null);
    setScreenFlash(null);
  }, [cards, clearScheduledTimeouts]);

  // Phase: entering → landed (auto after 800ms)
  useEffect(() => {
    if (phase === "entering") {
      const timer = setTimeout(() => setPhase("landed"), 800);
      return () => clearTimeout(timer);
    }
  }, [phase]);

  const bestPullRarity: Rarity = cards.reduce((best, card) => {
    const order: Rarity[] = ["common", "uncommon", "rare", "epic", "legendary"];
    return order.indexOf(card.character.rarity) > order.indexOf(best)
      ? card.character.rarity
      : best;
  }, "common" as Rarity);
  const currentCard = cards[currentIndex];
  const revealedCurrentCard = revealedCards.has(currentIndex) ? currentCard : undefined;

  const handleClick = useCallback(() => {
    if (isAnimating) return;

    if (phase === "landed") {
      setIsAnimating(true);
      setPhase("unwrapping");
      scheduleTimeout(() => {
        setPhase("revealing");
        setIsAnimating(false);
      }, 900);
      return;
    }

    if (phase === "revealing") {
      const card = cards[currentIndex];
      if (!card) return;

      if (!revealedCards.has(currentIndex)) {
        // Flip current card
        setIsAnimating(true);
        setRevealedCards((prev) => new Set(prev).add(currentIndex));
        setLastRarity(card.character.rarity);
        setMessage(getRevealMessage(card.character.rarity));
        setMessageKey((k) => k + 1);

        if (
          card.character.rarity === "epic" ||
          card.character.rarity === "legendary"
        ) {
          setScreenFlash(card.character.rarity);
          scheduleTimeout(() => setScreenFlash(null), 500);
        }

        scheduleTimeout(() => setIsAnimating(false), 600);
      } else if (currentIndex < cards.length - 1) {
        // Slide off current card, flip next
        setIsAnimating(true);
        setDismissedCards((prev) => new Set(prev).add(currentIndex));
        const nextIndex = currentIndex + 1;
        setCurrentIndex(nextIndex);

        // Flip next card after short delay
        scheduleTimeout(() => {
          const nextCard = cards[nextIndex];
          if (nextCard) {
            setRevealedCards((prev) => new Set(prev).add(nextIndex));
            setLastRarity(nextCard.character.rarity);
            setMessage(getRevealMessage(nextCard.character.rarity));
            setMessageKey((k) => k + 1);

            if (
              nextCard.character.rarity === "epic" ||
              nextCard.character.rarity === "legendary"
            ) {
              setScreenFlash(nextCard.character.rarity);
              scheduleTimeout(() => setScreenFlash(null), 500);
            }
          }
          scheduleTimeout(() => setIsAnimating(false), 600);
        }, 200);
      } else {
        // Last card already revealed → done
        setPhase("done");
      }
    }
  }, [isAnimating, phase, currentIndex, revealedCards, cards, scheduleTimeout]);

  const handleSkip = useCallback(() => {
    setPhase("done");
  }, []);

  const isInteractivePhase = phase === "landed" || phase === "revealing";
  const interactionLabel =
    phase === "landed" ? "Open pack" : "Reveal next card";
  const packId: PackId = pack?.id ?? "standard";
  const packName = pack?.name ?? "Standard Pack";
  const cardsPerPack = pack?.cardsPerPack ?? cards.length;
  const theme = PACK_THEMES[packId];
  const showPackVisual =
    phase === "entering" || phase === "landed" || phase === "unwrapping";
  const showSparkles = theme.sparkles && showPackVisual;

  return (
    <div
      className={`pack-opening-scene pack-theme-${packId} flex flex-col items-center justify-center gap-6 py-8 px-4 relative min-h-[80vh] select-none ${
        isInteractivePhase
          ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-xl"
          : ""
      }`}
      onClick={isInteractivePhase ? handleClick : undefined}
      onKeyDown={
        isInteractivePhase
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleClick();
              }
            }
          : undefined
      }
      role={isInteractivePhase ? "button" : undefined}
      tabIndex={isInteractivePhase ? 0 : undefined}
      aria-label={isInteractivePhase ? interactionLabel : undefined}
    >
      <div className={`pack-scene-backdrop ${theme.backdropClass}`} />

      {/* Screen flash */}
      {screenFlash && (
        <div
          className={`fixed inset-0 z-40 pointer-events-none animate-flash ${
            screenFlash === "legendary"
              ? "bg-yellow-400/60"
              : "bg-purple-500/50"
          }`}
        />
      )}

      {/* ===== ENTERING / LANDED / UNWRAPPING — Pack visual ===== */}
      {showPackVisual && (
        <div
          className={`relative z-10 cursor-pointer pack-shell pack-shell--${packId} ${
            phase === "entering" ? "pack-entering" : ""
          } ${phase === "landed" ? "pack-landed" : ""} ${
            phase === "unwrapping" ? "pack-unwrapping" : ""
          }`}
        >
          <div className={`pack-aura ${theme.auraClass}`} />
          <div className={`pack-aura-ring ${theme.auraRingClass}`} />
          {showSparkles && (
            <div className={`pack-sparkles pack-sparkles--${packId}`}>
              {Array.from({ length: 8 }).map((_, idx) => (
                <span key={idx} />
              ))}
            </div>
          )}

          <div className="absolute -top-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1">
            <Badge className={`pack-tier-pill pack-tier-pill--${packId}`}>
              {packName}
            </Badge>
            {theme.tagline && (
              <p className={`text-[10px] uppercase tracking-[0.24em] ${theme.subtitleClass}`}>
                {theme.tagline}
              </p>
            )}
          </div>

          {/* Pack wrapper (peels off on unwrap) */}
          <div className="pack-wrapper absolute inset-0 z-10 rounded-xl overflow-hidden pointer-events-none">
            <div className={`absolute inset-0 bg-gradient-to-br ${theme.wrapperGradientClass}`} />
            {/* Crosshair lines */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className={`absolute w-full h-px ${theme.crosshairClass}`} />
              <div className={`absolute h-full w-px ${theme.crosshairClass}`} />
            </div>
            {/* Sealed badge */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className={`w-16 h-16 rounded-full border-2 flex items-center justify-center rotate-[-12deg] ${theme.sealClass}`}>
                <span className="text-[10px] font-bold tracking-widest">
                  SEALED
                </span>
              </div>
            </div>
          </div>

          {/* Pack box */}
          <div className={`pack-box w-[200px] h-[280px] rounded-xl bg-gradient-to-b border flex flex-col items-center justify-center gap-3 shadow-2xl ${theme.boxGradientClass} ${theme.boxBorderClass}`}>
            <div className="text-center">
              <div className={`text-lg font-black tracking-tighter ${theme.titleClass}`}>
                MOG
              </div>
              <div className={`text-[10px] font-bold tracking-[0.3em] uppercase ${theme.subtitleClass}`}>
                Universe
              </div>
            </div>
            <div className={`w-12 h-px ${theme.dividerClass}`} />
            <div className={`text-xs font-semibold ${theme.cardsLabelClass}`}>
              {cardsPerPack.toLocaleString()} CARDS
            </div>
          </div>
        </div>
      )}

      {/* Tap to open prompt */}
      {phase === "landed" && (
        <p className={`text-sm animate-pulse mt-2 z-10 ${theme.promptClass}`}>
          Tap to open
        </p>
      )}

      {/* ===== REVEALING — Card stack ===== */}
      {phase === "revealing" && (
        <div className="flex flex-col items-center gap-5 relative z-10">
          {/* Rarity message */}
          {message && (
            <div key={messageKey} className="reveal-message text-center">
              <p
                className={`text-lg font-bold ${
                  lastRarity ? RARITY_COLOR[lastRarity] : ""
                }`}
              >
                {message}
              </p>
            </div>
          )}

          {/* Card stack container */}
          <div className={`relative card-md stack-appear ${getStatusOffsetClass(revealedCurrentCard)}`}>
            {cards.map((card, i) => {
              const isDismissed = dismissedCards.has(i);
              const isRevealed = revealedCards.has(i);
              const isCurrent = i === currentIndex;
              const depthFromCurrent = i - currentIndex;

              // Dismissed cards: render with slide-off animation
              if (isDismissed) {
                return (
                  <div
                    key={i}
                    className="absolute inset-0 card-slide-off"
                    style={{ zIndex: cards.length + 1 }}
                  >
                    <CharacterCard
                      character={card.character}
                      variant={card.variant}
                      revealed={true}
                    />
                  </div>
                );
              }

              // Skip cards already past or too deep in stack
              if (depthFromCurrent < 0) return null;
              if (depthFromCurrent > 3) return null;

              return (
                <div
                  key={i}
                  className="absolute inset-0 transition-all duration-300"
                  style={{
                    zIndex: cards.length - depthFromCurrent,
                    transform: `translateY(${depthFromCurrent * 4}px) scale(${
                      1 - depthFromCurrent * 0.02
                    })`,
                    opacity:
                      depthFromCurrent === 0
                        ? 1
                        : 0.6 - depthFromCurrent * 0.15,
                  }}
                >
                  {isCurrent && isRevealed ? (
                    <div className={`relative ${getStatusPaddingClass(card)}`}>
                      <div
                        className={`relative rounded-xl ${getGlowPulseClass(
                          card.character.rarity
                        )}`}
                        style={getGlowStyle(card.character.rarity, card.variant)}
                      >
                        <CharacterCard
                          character={card.character}
                          variant={card.variant}
                          revealed={true}
                        />
                      </div>
                      <CardStatusBadges card={card} />
                    </div>
                  ) : (
                    <CharacterCard
                      character={card.character}
                      variant={card.variant}
                      revealed={false}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Progress dots */}
          <div className="flex items-center gap-2 mt-2">
            {cards.map((card, i) => {
              const isRevealed = revealedCards.has(i);
              const isCurrent = i === currentIndex;
              const rarity = card.character.rarity;

              return (
                <div
                  key={i}
                  className={`rounded-full transition-all duration-300 ${
                    isRevealed
                      ? `w-3 h-3 ${DOT_COLOR[rarity]} shadow-sm ${DOT_GLOW[rarity]}`
                      : "w-2.5 h-2.5 bg-zinc-700"
                  } ${isCurrent ? "scale-125" : ""}`}
                />
              );
            })}
          </div>

          {/* Skip link */}
          {revealedCards.size > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleSkip();
              }}
              className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors mt-1"
            >
              Skip
            </button>
          )}
        </div>
      )}

      {/* ===== DONE — Results screen ===== */}
      {phase === "done" && (
        <div className="flex flex-col items-center gap-6 pack-results-enter relative z-10">
          <div className="flex flex-col items-center gap-1">
            <h2 className="text-2xl font-bold tracking-tight">Pack Results</h2>
            <Badge className={`pack-tier-pill pack-tier-pill--${packId}`}>
              {packName}
            </Badge>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {cards.map((card, i) => (
              <div
                key={i}
                className={`relative results-card-pop ${getStatusPaddingClass(card)}`}
                style={{
                  animationDelay: `${i * 100}ms`,
                  animationFillMode: "both",
                }}
              >
                <div
                  className="rounded-xl"
                  style={getGlowStyle(card.character.rarity, card.variant)}
                >
                  <CharacterCard
                    character={card.character}
                    variant={card.variant}
                    revealed={true}
                    compact
                  />
                </div>
                <CardStatusBadges card={card} />
              </div>
            ))}
          </div>

          <p className="text-sm text-muted-foreground">
            Best pull:{" "}
            <Badge
              className={
                bestPullRarity === "legendary"
                  ? "bg-gradient-to-r from-yellow-500 to-amber-500 text-black"
                  : bestPullRarity === "epic"
                    ? "bg-purple-600 text-white hover:bg-purple-600"
                    : bestPullRarity === "rare"
                      ? "bg-blue-600 text-white hover:bg-blue-600"
                      : bestPullRarity === "uncommon"
                        ? "bg-emerald-600 text-white hover:bg-emerald-600"
                        : ""
              }
            >
              {bestPullRarity}
            </Badge>
          </p>

          <div className="flex gap-2">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onOpenAnother();
              }}
              disabled={!canAfford || loading}
            >
              {loading ? "Opening..." : (openAnotherLabel ?? "Open Another")}
            </Button>
            <Button
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onDone();
              }}
            >
              View Collection
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
